import PropTypes from 'prop-types';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { OptimizelyProvider, useDecision } from '@optimizely/react-sdk';

import { getLocale } from '@edx/frontend-platform/i18n';
import { getOptimizely } from '../data/optimizely';

const OptimizelyExperimentController = (
  // Use defaultVariationKey if your experiment is set to target less than 100% of traffic
  // and you have a variation for users not included in the experiment.
  {
    defaultComponent: DefaultComponent,
    experimentalComponent: ExperimentalComponent,
    defaultVariationKey,
    experimentKey,
    ...props
  },
) => {
  const [decision] = useDecision(experimentKey);
  const { enabled, variationKey } = decision;

  const showDefault = !enabled || (defaultVariationKey && defaultVariationKey === variationKey);

  return (
    <div>
      {showDefault ? <DefaultComponent {...props} /> : <ExperimentalComponent {...props} />}
    </div>
  );
};

OptimizelyExperimentController.propTypes = {
  defaultComponent: PropTypes.elementType.isRequired,
  experimentalComponent: PropTypes.elementType.isRequired,
  defaultVariationKey: PropTypes.string,
  experimentKey: PropTypes.string.isRequired,
};

OptimizelyExperimentController.defaultProps = {
  defaultVariationKey: null,
};

/**
 * An optimizely helper:
 * Creates an OptimizelyProvider context for the given user and renders either experimentalComponent or
 * defaultCompoment based on the decision returned by optimizely.
 */
const OptimizelyExperiment = (props) => {
  const { userId } = getAuthenticatedUser();
  const { defaultComponent: DefaultComponent } = props;

  const optimizely = getOptimizely();

  return (
    optimizely
      ? (
        // The Optimizely SDK requires the user ID to be a string.
        <OptimizelyProvider
          optimizely={optimizely}
          user={{
            id: userId.toString(),
            attributes: { lms_language_preference: getLocale() },
          }}
        >
          <OptimizelyExperimentController {...props} />
        </OptimizelyProvider>
      )
      : (<DefaultComponent {...props} />)
  );
};

OptimizelyExperiment.propTypes = {
  defaultComponent: PropTypes.elementType.isRequired,
};

/**
 * An optimizely helper:
 * Creates an OptimizelyProvider context for the given user, but does not call decide to bucket the learner immediately.
 * Used if you need an optimizely context but don't want to immediately bucket the user.
 *
 * DefaultComponent is only rendered in the case that optimizely cannot be loaded.
 */
const OptimizelyProviderWrapper = ({
  defaultComponent: DefaultComponent,
  experimentalComponent: ExperimentalComponent,
  ...props
}) => {
  const { userId } = getAuthenticatedUser();
  const optimizely = getOptimizely();

  return (
    optimizely
      ? (
        // The Optimizely SDK requires the user ID to be a string.
        <OptimizelyProvider
          optimizely={optimizely}
          user={{
            id: userId.toString(),
            attributes: { lms_language_preference: getLocale() },
          }}
        >
          <ExperimentalComponent {...props} />
        </OptimizelyProvider>
      ) : <DefaultComponent {...props} />
  );
};

OptimizelyProviderWrapper.propTypes = {
  experimentalComponent: PropTypes.elementType.isRequired,
  defaultComponent: PropTypes.elementType.isRequired,
};

export default OptimizelyExperiment;

export {
  OptimizelyExperiment,
  OptimizelyProviderWrapper,
};
